import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { repoApi } from "../../api/repoApi";
import { analysisApi } from "../../api/analysisApi";
import { connectionApi } from "../../api/connectionApi";
import { getApiError } from "../../api/client";
import Badge from "../components/Badge.jsx";
import Card from "../components/Card.jsx";
import { ArrowRight, Plus, Link2 } from "lucide-react";
import "../styles/codebase.css";

function healthTone(score) {
  if (score >= 80) return "green";
  if (score >= 60) return "blue";
  if (score >= 40) return "yellow";
  return "red";
}

export default function RepoListPage() {
  const [repos, setRepos] = useState([]);
  const [healthMap, setHealthMap] = useState({});
  const [conns, setConns] = useState([]);

  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingConns, setLoadingConns] = useState(true);

  const loadRepos = async () => {
    setErr("");
    setLoading(true);
    try {
      const res = await repoApi.list(false);
      const items = res.data || [];
      setRepos(items);

      const entries = await Promise.allSettled(
        items.map((r) => analysisApi.getRepoHealth(r.id))
      );
      const hm = {};
      entries.forEach((p, idx) => {
        if (p.status === "fulfilled") hm[items[idx].id] = p.value.data;
      });
      setHealthMap(hm);
    } catch (e) {
      setErr(getApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadConnections = async () => {
    setErr("");
    setLoadingConns(true);
    try {
      const res = await connectionApi.list(false);
      setConns(res.data || []);
    } catch (e) {
      setErr(getApiError(e));
    } finally {
      setLoadingConns(false);
    }
  };

  const loadAll = async () => {
    await Promise.allSettled([loadConnections(), loadRepos()]);
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="cb-page">
      <div className="page-head">
        <div>
          <h2>Repositories</h2>
          <div className="muted">
            Connect GitHub / Azure DevOps / SAP / Oracle Apps / Salesforce / NetSuite / ServiceNow
          </div>
        </div>

        <div className="page-actions">
          <Link className="btn" to="/codebase/connections">
            <Link2 size={16} /> Connections
          </Link>

          <Link className="btn primary" to="/codebase/repos/new">
            <Plus size={16} /> Add Repository
          </Link>

          <button className="btn" onClick={loadAll} disabled={loading || loadingConns}>
            {loading || loadingConns ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      {/* ✅ Connections section */}
      <Card
        title="Connections (saved & encrypted)"
        subtitle="These are the credentials/profiles you created in the Connections page."
        right={
          <Link className="btn primary" to="/codebase/connections/new">
            <Plus size={16} /> Add Connection
          </Link>
        }
      >
        <div className="table-wrap">
          <table className="cb-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Provider</th>
                <th>Base URL</th>
                <th>Status</th>
                <th style={{ width: 180, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loadingConns && conns.length === 0 ? (
                <tr>
                  <td colSpan={5} className="muted" style={{ padding: 14 }}>
                    No connections yet. Create one to store secrets securely.
                  </td>
                </tr>
              ) : (
                conns.map((c) => (
                  <tr key={c.id}>
                    <td className="mono">{c.name}</td>
                    <td>{c.provider}</td>
                    <td className="mono small">{c.base_url || "—"}</td>
                    <td>{c.is_active ? "Active" : "Inactive"}</td>
                    <td style={{ textAlign: "right" }}>
                      <Link className="btn ghost" to={`/codebase/connections/${c.id}/edit`}>
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ✅ Repos section */}
      <div className="cb-table-wrap">
        <div className="cb-table-toolbar">
          <div className="muted small">
            {loading ? "Loading…" : `${repos.length} repository(s)`}
          </div>
        </div>

        <table className="cb-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Provider</th>
              <th>Branch</th>
              <th>Repo URL</th>
              <th>Last Scan</th>
              <th style={{ width: 140 }}>Health</th>
              <th style={{ width: 220, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && repos.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted" style={{ padding: 16 }}>
                  No repositories yet. Click <b>Add Repository</b>.
                </td>
              </tr>
            ) : (
              repos.map((r) => {
                const h = healthMap[r.id];
                const score = h?.health_score ?? 0;
                return (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontWeight: 900 }}>{r.name}</div>
                      <div className="muted small">{r.description || ""}</div>
                    </td>
                    <td className="mono">{r.provider}</td>
                    <td className="mono">{r.default_branch || "main"}</td>
                    <td className="mono small">{r.repo_url}</td>
                    <td className="muted small">
                      {h?.last_scan_at ? new Date(h.last_scan_at).toLocaleString() : "—"}
                    </td>
                    <td>
                      <Badge tone={healthTone(score)}>Health {score}</Badge>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div className="cb-actions">
                        <Link className="btn" to={`/codebase/repos/${r.id}`}>
                          Open <ArrowRight size={16} />
                        </Link>
                        <Link className="btn ghost" to={`/codebase/repos/${r.id}/edit`}>
                          Edit
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
